/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Spinner,
  Flex,
  Badge,
  Text,
  Box,
  HStack,
} from '@chakra-ui/react';
import { toast } from 'sonner';
import { useIcms } from '../../../../../../hooks/useIcms';
import {
  ClassificacaoAcumulador,
  IAcumuladorBulkPayload,
  IAcumuladorSugerido,
  TipoOperacaoAcumulador,
} from '../../../../../../types/icms';

interface AcumuladoresSugeridosDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  codiEmp: number;
  dtInicio: string;
  dtFim: string;
}

interface RowState {
  selected: boolean;
  classificacao: ClassificacaoAcumulador;
  tipo_operacao: TipoOperacaoAcumulador;
}

export function AcumuladoresSugeridosDrawer({
  isOpen,
  onClose,
  codiEmp,
  dtInicio,
  dtFim,
}: AcumuladoresSugeridosDrawerProps) {
  const queryClient = useQueryClient();
  const { getAcumuladoresSugeridos, getClassificacoesAcumulador, upsertAcumuladoresBulk } =
    useIcms();

  const { data: classificacoes } = useQuery({
    queryKey: ['icms-classificacoes-acumulador'],
    queryFn: getClassificacoesAcumulador,
    staleTime: Infinity,
    enabled: isOpen,
  });

  const { data: sugeridos, isFetching } = useQuery({
    queryKey: ['acumuladores-sugeridos', { codi_emp: codiEmp, dt_inicio: dtInicio, dt_fim: dtFim }],
    queryFn: getAcumuladoresSugeridos,
    enabled: isOpen,
  });

  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  useEffect(() => {
    if (sugeridos) {
      const next: Record<string, RowState> = {};
      sugeridos.forEach(s => {
        next[s.codi_acu] = {
          selected: false,
          classificacao: s.classificacao_atual ?? 'outras',
          tipo_operacao: s.tipo_operacao,
        };
      });
      setRowState(next);
    }
  }, [sugeridos]);

  const updateRow = (codiAcu: string, patch: Partial<RowState>) => {
    setRowState(prev => ({ ...prev, [codiAcu]: { ...prev[codiAcu], ...patch } }));
  };

  const allSelected = useMemo(() => {
    if (!sugeridos || sugeridos.length === 0) return false;
    return sugeridos.every(s => rowState[s.codi_acu]?.selected);
  }, [sugeridos, rowState]);

  const toggleAll = () => {
    setRowState(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        next[k] = { ...next[k], selected: !allSelected };
      });
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const itens = (sugeridos ?? [])
        .filter(s => rowState[s.codi_acu]?.selected)
        .map(s => ({
          codi_acu: s.codi_acu,
          nome_acu: s.nome_acu,
          tipo_operacao: rowState[s.codi_acu].tipo_operacao,
          classificacao: rowState[s.codi_acu].classificacao,
        }));
      const payload: IAcumuladorBulkPayload = { itens };
      return upsertAcumuladoresBulk(codiEmp, payload);
    },
    onSuccess: result => {
      toast.success(`${result.length} acumuladores classificados`);
      queryClient.invalidateQueries({ queryKey: ['acumuladores'] });
      queryClient.invalidateQueries({ queryKey: ['acumuladores-sugeridos'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao classificar. Tente novamente.');
    },
  });

  const selectedCount = Object.values(rowState).filter(r => r.selected).length;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Descobrir acumuladores</DrawerHeader>
        <DrawerBody>
          <Text fontSize="12px" color="gray.500" mb={3}>
            Lista de códigos de acumulador encontrados nas notas da empresa entre {dtInicio} e{' '}
            {dtFim}. Selecione os que deseja classificar e envie em lote.
          </Text>
          {isFetching && !sugeridos ? (
            <Flex justify="center" py={6}>
              <Spinner color="primary" />
            </Flex>
          ) : !sugeridos || sugeridos.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={6}>
              Nenhum acumulador encontrado nas notas do período.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>
                      <Checkbox
                        isChecked={allSelected}
                        onChange={toggleAll}
                        isDisabled={!sugeridos.length}
                      />
                    </Th>
                    <Th>Cód</Th>
                    <Th>Nome</Th>
                    <Th>Notas</Th>
                    <Th>Operação</Th>
                    <Th>Classificação</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sugeridos.map((s: IAcumuladorSugerido) => {
                    const state = rowState[s.codi_acu];
                    if (!state) return null;
                    return (
                      <Tr key={s.codi_acu}>
                        <Td>
                          <Checkbox
                            isChecked={state.selected}
                            onChange={e => updateRow(s.codi_acu, { selected: e.target.checked })}
                          />
                        </Td>
                        <Td fontWeight="bold">{s.codi_acu}</Td>
                        <Td fontSize="11px">{s.nome_acu}</Td>
                        <Td isNumeric>{s.qtd_notas}</Td>
                        <Td>
                          <select
                            value={state.tipo_operacao}
                            onChange={e =>
                              updateRow(s.codi_acu, {
                                tipo_operacao: e.target.value as TipoOperacaoAcumulador,
                              })
                            }
                            style={{
                              fontSize: '11px',
                              padding: '4px 6px',
                              borderRadius: '4px',
                              border: '1px solid #E2E8F0',
                            }}
                          >
                            <option value="entrada">entrada</option>
                            <option value="saida">saida</option>
                          </select>
                        </Td>
                        <Td>
                          <select
                            value={state.classificacao}
                            onChange={e =>
                              updateRow(s.codi_acu, {
                                classificacao: e.target.value as ClassificacaoAcumulador,
                              })
                            }
                            style={{
                              fontSize: '11px',
                              padding: '4px 6px',
                              borderRadius: '4px',
                              border: '1px solid #E2E8F0',
                            }}
                          >
                            {(classificacoes ?? []).map(c => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </Td>
                        <Td>
                          {s.ja_classificado ? (
                            <Badge colorScheme="green" fontSize="9px">
                              já classificado
                            </Badge>
                          ) : (
                            <Badge colorScheme="orange" fontSize="9px">
                              novo
                            </Badge>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </DrawerBody>
        <DrawerFooter>
          <HStack spacing={3} w="100%">
            <Text fontSize="12px" color="gray.600" flex={1}>
              {selectedCount} selecionado(s)
            </Text>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              isLoading={mutation.isPending}
              isDisabled={selectedCount === 0}
              onClick={() => mutation.mutate()}
            >
              Classificar selecionados
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
